import moment from 'moment';
import React, { useCallback, useContext } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import Button from 'kingpin/atoms/Button';

import formatFloat from '../../../api/getChartSeries/formatFloat';
import Row from '../../../components/Common/Row';
import { getKpiTargetForMonth } from '../../../components/Goals/GeneralGoalForm/KpiForm/AdvancedTargetInput/MonthlyTargetBuilder';
import KpiForm from '../../../components/Goals/GeneralGoalForm/KpiForm';
import HideUnlessMouseOver from '../../../components/HideUnlessMouseOver';
import { DeltaNumber } from '../../../components/OnTargetCellRenderer';
import PerformanceCell from '../../../components/PerformanceTargetsBoard/PerformanceCell/PerformanceCell';
import PermissionGates from '../../../components/PermissionGates';
import { TooltipContent } from '../../../components/ProgressCellRenderer';
import Tooltip from '../../../components/Tooltip';
import Cell from '../../../components/V5Gadget/Matrix/Cell';
import { AccountTodayOverrideContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import BoardsContext from '../../../contexts/BoardsContext';
import GoalContext from '../../../contexts/GoalContext';
import PopupContext from '../../../contexts/PopupContext';
import ReportsContext from '../../../contexts/ReportsContext';
import updateGoal from '../../../hooks/goals/updateGoal';
import usePopup from '../../../hooks/usePopup';
import useValueFormatters from '../../../hooks/useValueFormatters';
import PERMISSIONS from '../../../permissionDefinitions';
import metricTypeCheckers from '../../../types/metricTypeCheckers';
import visTypeCheckers from '../../../types/visTypeCheckers';

import GoalIntervalRenderer from '../GoalIntervalRenderer';
import { KPICellParams, KPINameParams } from './types';

const useKpi = (params: KPICellParams) => {
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { lastCompletedDateBucket, currentDateBucket, goal } =
    useContext(GoalContext);
  const { date, isCard } = params.colDef.cellRendererParams;
  const { formatMetric } = useValueFormatters();

  const kpiSetting = params.node.data.kpiSetting as GoalKPI;
  const metric = params.node.data.metric as Metrics.Metric;

  const { formatting } = metric;
  const targetMode = kpiSetting ? kpiSetting.targetMode : undefined;
  const expected = (() => {
    if (kpiSetting.targetMode === undefined) {
      return undefined;
    }

    const baseExpected = (() => {
      if (targetMode === 'basic' && kpiSetting.basicTarget !== undefined) {
        return kpiSetting.basicTarget;
      } else if (targetMode === 'advanced' && kpiSetting.advancedTarget) {
        return getKpiTargetForMonth(kpiSetting.advancedTarget, date);
      } else {
        return 0;
      }
    })();
    const isCurrentMonth = currentDateBucket === date;
    const isProrateEligible = metricTypeCheckers.isNormalMetric(metric)
      ? metric.aggFunc === 'sum'
      : false;
    if (isCurrentMonth && isProrateEligible) {
      if (goal.cadence !== 'week' && goal.cadence !== 'month') {
        const err = new Error('');
        err.name = `Unsupported goal cadence: ${goal.cadence}`;
        throw err;
      }

      const divideBy = goal.cadence === 'week' ? 7 : moment(date).daysInMonth();
      const expectedPerDay = baseExpected / divideBy;
      const now = moment(todayOverride);
      const elapsedDays = Math.abs(now.diff(moment(date), 'days')) + 1;
      return formatFloat(expectedPerDay * elapsedDays, formatting.precision);
    } else {
      return formatFloat(baseExpected, formatting.precision);
    }
  })();
  const actual =
    params.value === null || params.value === undefined
      ? null
      : formatFloat(params.value, formatting.precision);
  const isGood = formatting.positiveDeltaIsGood
    ? actual >= expected
    : actual <= expected;
  const diff = actual - expected;
  const adverb = (() => {
    if (isGood) {
      if (formatting.positiveDeltaIsGood) {
        return 'ahead of';
      } else {
        return 'behind';
      }
    } else {
      if (formatting.positiveDeltaIsGood) {
        return 'behind';
      } else {
        return 'ahead of';
      }
    }
  })();
  const actualFormatted = formatMetric({
    value: params.value,
    metricId: metric.id,
  });
  const expectedFormatted = formatMetric({
    value: expected,
    metricId: metric.id,
  });
  const diffFormatted = formatMetric({
    value: Math.abs(diff),
    metricId: metric.id,
  });
  const onTargetText = `${diffFormatted} ${adverb} target`;
  const popupText = `${actualFormatted} / ${expectedFormatted}`;
  const color =
    targetMode === undefined
      ? 'var(--color-text-primary)'
      : isGood
        ? 'var(--color-status-success)'
        : 'var(--color-status-danger)';
  const hasActual = actual !== null;

  return {
    hasActual,
    isCurrentMonth: date === lastCompletedDateBucket && !isCard,
    date,
    popupText,
    onTargetText,
    isGood,
    diff,
    metricName: metric.name,
    actualFormatted,
    expectedFormatted,
    color,
    kpiSetting,
    targetMode,
  };
};

const EditButton = ({ kpi }: { kpi: GoalKPI }) => {
  const { goal } = useContext(GoalContext);
  const { selectedAccountId } = useContext(AccountPickerContext);

  const { isOpen, open, close } = usePopup();

  const onSave = useCallback(
    async (newKpi: GoalKPI) => {
      const newGoal = {
        ...goal,
        kpis: goal.kpis.map((i) => {
          if (i.id === newKpi.id) {
            return newKpi;
          }
          return i;
        }),
      };

      return updateGoal(newGoal, selectedAccountId).then(() => {
        close();
      });
    },
    [selectedAccountId, close, goal],
  );

  return (
    <div className="showOnMouseOver">
      <PermissionGates.Has requiredPermission={PERMISSIONS.GOALS.CRUD_GOALS}>
        <Button
          onClick={(event) => {
            open();
            event.stopPropagation();
            event.preventDefault();
          }}
          icon="edit-filled"
          size={'Small'}
          type="Ghost"
        />

        <ModalTransition>
          {isOpen && (
            <Modal shouldScrollInViewport={false} autoFocus={false}>
              <KpiForm
                kpi={kpi}
                onClose={close}
                cadence={goal.cadence}
                onSave={onSave}
                startDate={goal.fixedStartDate}
                endDate={
                  visTypeCheckers.isGeneralGoal(goal)
                    ? goal.fixedEndDate
                    : undefined
                }
              />
            </Modal>
          )}
        </ModalTransition>
      </PermissionGates.Has>
    </div>
  );
};

export const KpiNameRenderer = (params: KPINameParams) => {
  const kpiSetting = params.node.data.kpiSetting as GoalKPI | undefined;
  const rowType = params.node.data.rowType as 'goal' | 'kpi heading' | 'kpi';

  if (rowType === 'kpi heading') {
    return (
      <Cell
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          width: 'calc(100% + 13px)',
          height: '32px',
          marginLeft: -6,
          marginRight: -6,
          padding: 5,
          borderBottom: '1px solid var(--color-border-secondary)',
          borderTop: '1px solid var(--color-border-secondary)',
        }}
      >
        <span>KPIs</span>
      </Cell>
    );
  }

  if (!kpiSetting) {
    return null;
  }

  return (
    <HideUnlessMouseOver>
      <Cell>
        <Row centerAlign>
          <div style={{ marginRight: 8 }}>
            <span>{params.value}</span>
          </div>
          {rowType === 'kpi' && <EditButton kpi={kpiSetting} />}
        </Row>
      </Cell>
    </HideUnlessMouseOver>
  );
};

export const TrendCellRenderer = (params: KPICellParams) => {
  const rowType = params.node.data.rowType as 'goal' | 'kpi heading' | 'kpi';
  if (rowType === 'kpi') {
    return <KpiCellRenderer {...params} />;
  } else if (rowType === 'goal') {
    return <GoalIntervalRenderer {...params} />;
  } else {
    return (
      <Cell
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          width: 'calc(100% + 13px)',
          height: '32px',
          marginLeft: -6,
          marginRight: -6,
          borderBottom: '1px solid var(--color-border-secondary)',
          borderTop: '1px solid var(--color-border-secondary)',
        }}
      />
    );
  }
};

const KpiCellRenderer = (params: KPICellParams) => {
  const {
    isCurrentMonth,
    popupText,
    onTargetText,
    isGood,
    diff,
    metricName,
    actualFormatted,
    color,
    hasActual,
    expectedFormatted,
    targetMode,
    kpiSetting,
    date,
  } = useKpi(params);
  const { goal } = useContext(GoalContext);
  const { allReports } = useContext(ReportsContext);
  const { boards } = useContext(BoardsContext);
  const { openPopupReport, openPopupBoard } = useContext(PopupContext);

  const hasDrillDown =
    kpiSetting.reportDrillDownId || kpiSetting.boardDrillDownId;
  const onClick = useCallback(() => {
    if (!kpiSetting.reportDrillDownId && !kpiSetting.boardDrillDownId) {
      return;
    }
    if (!goal) {
      return;
    }

    const startDate = date;
    const endDate = moment(date)
      .endOf(goal.cadence === 'week' ? 'week' : 'month')
      .format('YYYY-MM-DD');

    if (kpiSetting.reportDrillDownId) {
      const report = allReports.find(
        (r) => r.id === kpiSetting.reportDrillDownId,
      );
      if (report) {
        const unSavedFilter: UnSavedFilter = {
          drillDowns: kpiSetting.drillDowns,
          dateScope: {},
          scope: [],
          dateField: kpiSetting.dateField || 'date',
          dateRange: {
            startDate,
            endDate,
          },
        };
        openPopupReport({
          drillDowns: [],
          origin: 'Goal',
          selectedReport: report,
          unSavedFilter,
        });
      }
    } else if (kpiSetting.boardDrillDownId) {
      const board = boards.find((b) => b.id === kpiSetting.boardDrillDownId);
      if (board) {
        const dateRange = {
          startDate,
          endDate,
        };
        openPopupBoard({
          popupFilters: kpiSetting.drillDowns,
          scope: [],
          popupDateRange: dateRange,
          origin: 'Scorecard',
          selectedBoard: board,
        });
      }
    }
  }, [
    allReports,
    boards,
    date,
    goal,
    kpiSetting.boardDrillDownId,
    kpiSetting.dateField,
    kpiSetting.drillDowns,
    kpiSetting.reportDrillDownId,
    openPopupBoard,
    openPopupReport,
  ]);

  if (!hasActual) {
    if (targetMode === undefined) {
      return null;
    }
    return (
      <PerformanceCell
        isCurrentMonth={isCurrentMonth}
        onClick={hasDrillDown ? onClick : undefined}
        isInput
      >
        <DeltaNumber color={'var(--color-grey-4)'}>
          {expectedFormatted}
        </DeltaNumber>
      </PerformanceCell>
    );
  }

  return (
    <PerformanceCell
      isCurrentMonth={isCurrentMonth}
      onClick={hasDrillDown ? onClick : undefined}
      isInput
    >
      <Tooltip
        isAltTooltip
        content={
          targetMode === undefined ? null : (
            <TooltipContent
              progressText={popupText}
              onTargetText={onTargetText}
              isGood={isGood}
              delta={diff}
              tooltipTitle={metricName}
            />
          )
        }
      >
        <Row centerAlign style={{ justifyContent: 'flex-end' }}>
          <DeltaNumber color={color}>{actualFormatted}</DeltaNumber>
        </Row>
      </Tooltip>
    </PerformanceCell>
  );
};
